import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { durakAPI } from '../api/durakApi';
import { gameAPI } from '../../brawl/api/gameApi';
import useDurakSession from '../hooks/useDurakSession';
import DurakTable from '../components/DurakTable';
import { DurakLobby, DurakResult, DurakSearching } from '../components/DurakScreens';
import DatumPassRequiredModal, { PASS_REQUIRED_CODE } from '../../common/DatumPassRequiredModal';
import { InviteBanner, InviteWaiting } from '../../common/InviteRoom';
import '../styles/durak.css';
import { useTranslation } from 'react-i18next';
import i18n from '../../../../i18n';
import { gameErrorText } from '../../common/gameErrors';

/**
 * Online Durak (podkidnoy, 2 players) for GRAM stakes — SPEC_V141_GAMES.md §11.4.
 * lobby → searching → table → result. Full-screen route outside AppLayout, like the Arena.
 * «Play with a friend»: lobby → room (invite link) → table; the friend opens /durak?room=CODE → Accept.
 */

const errMsg = (e) => e?.response?.data?.message || e?.response?.data?.error || e?.message
  || i18n.t('common.failed_to_load', 'Failed to load');

export default function DurakPage({ createSocket }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const session = useDurakSession({ createSocket });
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [stake, setStake] = useState('0');
  const [confirmLeave, setConfirmLeave] = useState(false);
  const [passModal, setPassModal] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const roomCode = searchParams.get('room');
  const [invite, setInvite] = useState(null); // friend's side: { loading, data, error }

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await durakAPI.getInfo();
      setInfo(data);
      setStake((cur) => {
        const list = data?.stakes || ['0'];
        return list.some((x) => String(x) === String(cur)) ? cur : String(list[0] ?? '0');
      });
    } catch (e) {
      setLoadError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // the server refused a GRAM stake (no DatumPass / it just expired) → explain in the popup
  const sessionError = session.error;
  useEffect(() => {
    if (sessionError?.code !== PASS_REQUIRED_CODE) return;
    setStake('0');
    setPassModal(true);
    load();
  }, [sessionError, load]);

  const findGame = () => {
    if (Number(stake) > 0 && info?.datumPassActive !== true) setPassModal(true);
    else session.findGame(stake);
  };

  // ── play with a friend ──
  useEffect(() => {
    if (!roomCode) {
      setInvite(null);
      return undefined;
    }
    let alive = true;
    setInvite({ loading: true });
    gameAPI.getRoom(roomCode)
      .then((data) => {
        if (!alive) return;
        if (data?.game === 'ARENA') {
          navigate(`/arena?room=${encodeURIComponent(roomCode)}`, { replace: true });
          return;
        }
        setInvite({ data });
      })
      .catch((e) => {
        if (!alive) return;
        setInvite({ error: e?.response?.status === 404 ? gameErrorText(t, { code: 'ROOM_NOT_FOUND' }) : errMsg(e) });
      });
    return () => { alive = false; };
  }, [roomCode, navigate, t]);

  const dismissInvite = () => {
    setInvite(null);
    setSearchParams({}, { replace: true });
  };

  const inviteFriend = () => {
    if (Number(stake) > 0 && info?.datumPassActive !== true) setPassModal(true);
    else session.createRoom(stake);
  };

  const acceptInvite = () => {
    const data = invite?.data;
    if (!data) return;
    if (Number(data.stake) > 0 && info?.datumPassActive !== true) {
      setPassModal(true);
      return;
    }
    dismissInvite();
    session.joinRoom(data.code);
  };
  const inviteStake = Number(invite?.data?.stake) || 0;
  const inviteTooExpensive = inviteStake > (Number(info?.tonBalance) || 0);

  // a game still running on the server (reload / came back to the page) → straight back to the table
  const { screen, resume } = session;
  const resumedRef = useRef(null);
  useEffect(() => {
    const id = info?.activeMatchId;
    if (id && screen === 'lobby' && resumedRef.current !== id) {
      resumedRef.current = id;
      resume();
    }
  }, [info, screen, resume]);

  // refresh balance + recent games whenever we come back to the lobby or a game ends
  useEffect(() => {
    if (screen === 'result') load();
  }, [screen, load]);

  const goBack = () => {
    if (screen === 'game') {
      setConfirmLeave(true);
      return;
    }
    if (screen === 'searching') session.cancelSearch();
    if (screen === 'room') session.cancelRoom();
    navigate('/lair', { state: { tab: 'Arena' } });
  };

  const toLobby = () => {
    session.backToLobby();
    load();
  };

  const showHeader = screen !== 'game';

  return (
    <div className="dk-page">
      {showHeader && (
        <header className="dk-header">
          <button type="button" className="dk-icon-btn" onClick={goBack} aria-label={t('common.back', 'Back')}>
            <svg viewBox="0 0 24 24"><path d="M15 5 8 12l7 7" /></svg>
          </button>
          <div className="dk-header__title">{t('durak.header', 'DURAK')}</div>
          <div className="dk-header__spacer" />
        </header>
      )}

      {screen === 'lobby' && (
        <div className="dk-scroll">
          <DurakLobby
            info={info}
            loading={loading}
            error={loadError}
            stake={stake}
            setStake={setStake}
            onFind={findGame}
            onInvite={inviteFriend}
            onRetry={load}
            banner={invite && (
              <InviteBanner
                invite={invite.data}
                loading={invite.loading}
                error={invite.error}
                onAccept={acceptInvite}
                onDismiss={dismissInvite}
                acceptDisabled={inviteTooExpensive}
                acceptHint={inviteTooExpensive ? t('pvp.not_enough_gram', 'Not enough GRAM for this stake') : null}
              />
            )}
            notice={session.error?.code === PASS_REQUIRED_CODE || !session.error ? null : gameErrorText(t, session.error)}
            onPassRequired={() => setPassModal(true)}
          />
        </div>
      )}

      {screen === 'searching' && (
        <DurakSearching
          queue={session.queue}
          connection={session.connection}
          onCancel={session.cancelSearch}
          joining={session.joining}
        />
      )}

      {screen === 'room' && (
        <InviteWaiting game="durak" room={session.room} connection={session.connection} onCancel={session.cancelRoom} />
      )}

      {screen === 'game' && session.state && (
        <DurakTable
          match={session.match}
          state={session.state}
          table={session.table}
          fx={session.fx}
          send={session.send}
          onSurrender={() => setConfirmLeave(true)}
          connection={session.connection}
          error={session.error}
        />
      )}
      {screen === 'game' && !session.state && <div className="dk-center"><div className="dk-spinner" /></div>}

      {screen === 'result' && session.end && (
        <DurakResult
          end={session.end}
          match={session.match}
          onAgain={() => session.findGame(session.match?.stake ?? stake)}
          onLobby={toLobby}
        />
      )}

      {screen === 'error' && (
        <div className="dk-center">
          <div className="dk-muted">
            {session.error && session.error.code !== 'CONNECTION' ? gameErrorText(t, session.error) : t('pvp.connection_lost', 'Connection lost')}
          </div>
          <button type="button" className="app-btn app-btn--purple app-btn--sm dk-auto" onClick={toLobby}>{t('arena.back_to_lobby', 'Back to lobby')}</button>
        </div>
      )}

      {confirmLeave && screen === 'game' && (
        <div className="dk-modal" role="dialog" aria-modal="true">
          <div className="dk-modal__box">
            <div className="dk-modal__title">{t('durak.surrender_q', 'Surrender?')}</div>
            <div className="dk-muted">
              {Number(session.match?.stake) > 0 ? t('arena.lose_stake', 'You will lose your stake.') : t('durak.counts_as_loss', 'The game counts as a loss.')}
            </div>
            <div className="dk-modal__actions">
              <button type="button" className="app-btn app-btn--purple app-btn--sm" onClick={() => setConfirmLeave(false)}>{t('durak.play_on', 'Play on')}</button>
              <button
                type="button"
                className="app-btn app-btn--stop app-btn--sm"
                onClick={() => {
                  setConfirmLeave(false);
                  session.surrender();
                }}
              >
                {t('durak.surrender', 'Surrender')}
              </button>
            </div>
          </div>
        </div>
      )}

      {passModal && (
        <DatumPassRequiredModal
          onClose={() => setPassModal(false)}
          onPlayFree={() => { setStake('0'); setPassModal(false); }}
          onPurchased={load}
        />
      )}
    </div>
  );
}
